import type { FetchError } from 'ofetch'
import { defineStore } from 'pinia'

import { usePaymentWidget } from '~/composables/payment-widget'
import constants from '~/constants'
import { OrderPaymentMethod } from '~/enums/order-payment-method'
import OrderShippingType from '~/enums/order-shipping-type'
import Page from '~/enums/page'
import helpers from '~/helpers'
import ApiErrorHandler from '~/helpers/api-error-handler'
import JsonApiHelper from '~/helpers/json-api-helper'
import EcommerceApiService from '~/services/ecommerce-api-service'
import { useProductsStore } from '~/store/products'
import type {
  CartOrderPaymentMethod,
  CartOrderShippingType,
  CartProductData,
  EcommerceCartItem,
  GetCartProductsParams,
  Gift,
  Notification
} from '~/types/cart'
import type { CreateOrderRequestData } from '~/types/orders'
import type {
  DalliCourierData,
  PickupPointShippingData,
  RussianPostCourierShippingData,
  RussianPostInternationalParcelShippingData,
  ShippingData,
  UserShippingData
} from '~/types/shipping'

type Form = {
  // Agreements.
  isAgreeToPreOrder: boolean
  isAgreeToTermsAndConditions: boolean

  // Discounts.
  certificateName: string | null
  password: string | null
  passwordConfirm: string | null
  pointsToSpend: number | null
  promoCodeName: string | null
  superminsPointsToSpend: number | null

  // Shipping.
  shippingAddressLine1: string | null
  shippingAddressLine2: string | null
  shippingCity: string | null
  shippingCountry: string | null
  shippingEmail: string | null
  shippingFirstName: string | null
  shippingLastName: string | null
  shippingPhoneNumber: string | null
  shippingPostcode: string | null
  shippingState: string | null
}

type State = {
  backToSchoolStepAmountInKopecks: number | null
  cartOrderPaymentMethods: CartOrderPaymentMethod[]
  cartOrderShippingTypes: CartOrderShippingType[]
  cartProductsData: CartProductData[]
  discountAmountInKopecks: number | null
  errorDetail: string | null
  form: Form
  gifts: Gift[]
  goldenTicketStepAmountInKopecks: number | null
  initialPage: Page | null
  isCertificateApplied: boolean
  isCertificateApplying: boolean
  isDataLoading: boolean
  isDataLoading2: boolean
  isDataLoadingError: boolean
  isFreeShippingAllowed: boolean | null
  isFreeShippingOnFirstOrderAllowed: boolean | null
  isFreeShippingOnFirstOrderEnabled: boolean | null
  isOrderCreating: boolean
  isPointsApplying: boolean
  isPromoCodeApplied: boolean
  isPromoCodeApplying: boolean
  notifications: Notification[]
  orderPaymentMethod: OrderPaymentMethod | null
  orderShippingType: OrderShippingType | null
  shippingCostInKopecks: number | null
  shippingData: ShippingData | null
  urgentOrderPriceInKopecks: number | null
  userShippingData: UserShippingData | null
  weightInGrams: number | null

  // Order tags.
  isBloggerOrder: boolean
  isRepeatOrder: boolean
  isUrgentOrder: boolean

  // Points.
  arePointsApplied: boolean
  cashbackInPoints: number | null
  maxPointsForOrder: number | null
  userTotalPoints: number | null

  // Supermins points.
  areSuperminsPointsApplied: boolean
  cashbackInSuperminsPoints: number | null
  maxSuperminsPointsForOrder: number | null
  userTotalSuperminsPoints: number | null
}

const initialState = (): State => ({
  backToSchoolStepAmountInKopecks: null,
  cartOrderPaymentMethods: [],
  cartOrderShippingTypes: [],
  cartProductsData: [],
  discountAmountInKopecks: null,
  errorDetail: null,
  form: {
    // Agreements.
    isAgreeToPreOrder: false,
    isAgreeToTermsAndConditions: true,

    // Discounts
    certificateName: null,
    password: null,
    passwordConfirm: null,
    pointsToSpend: null,
    promoCodeName: null,
    superminsPointsToSpend: null,

    // Shipping.
    shippingAddressLine1: null,
    shippingAddressLine2: null,
    shippingCity: null,
    shippingCountry: null,
    shippingEmail: null,
    shippingFirstName: null,
    shippingLastName: null,
    shippingPhoneNumber: null,
    shippingPostcode: null,
    shippingState: null
  },
  gifts: [],
  goldenTicketStepAmountInKopecks: null,
  initialPage: null,
  isCertificateApplied: false,
  isCertificateApplying: false,
  isDataLoading: false,
  isDataLoading2: false,
  isDataLoadingError: false,
  isFreeShippingAllowed: false,
  isFreeShippingOnFirstOrderAllowed: false,
  isFreeShippingOnFirstOrderEnabled: false,
  isOrderCreating: false,
  isPointsApplying: false,
  isPromoCodeApplied: false,
  isPromoCodeApplying: false,
  notifications: [],
  orderPaymentMethod: null,
  orderShippingType: null,
  shippingCostInKopecks: null,
  shippingData: null,
  urgentOrderPriceInKopecks: null,
  userShippingData: null,
  weightInGrams: null,

  // Order tags.
  isBloggerOrder: false,
  isRepeatOrder: false,
  isUrgentOrder: false,

  // Points.
  arePointsApplied: false,
  cashbackInPoints: null,
  maxPointsForOrder: null,
  userTotalPoints: null,

  // Supermins points.
  areSuperminsPointsApplied: false,
  cashbackInSuperminsPoints: null,
  maxSuperminsPointsForOrder: null,
  userTotalSuperminsPoints: null
})

const messages = {
  // Promo code.
  promoCodeIsApplied: 'Промокод применён.',
  promoCodeIsCanceled: 'Промокод отменён.',

  // Points.
  redemptionOfPointsHasBeenCanceled: 'Списание баллов iTAB Плюс отменено.',
  redemptionOfSuperminsPointsHasBeenCanceled: 'Списание баллов Supermins отменено.',

  // Certificate.
  certificateIsCanceled: 'Сертификат отменён.',

  // Validation.
  countryIsNotSelected: 'Необходимо выбрать страну.',
  failedToGetCartProducts: 'Не удалось получить товары корзины.',
  localPickUpPointIsNotSelected: 'Необходимо выбрать пункт самовывоза.',
  orderSubtotalAmountIsTooSmall: 'Сумма товаров в заказе слишком мала.',
  addressIsNotFound: 'Извините, адрес не найден.',
  addressIsNotSelected: 'Необходимо выбрать адрес.',
  pickUpPointIsNotSelected: 'Необходимо выбрать пункт выдачи заказов.',
  shippingTypeIsNotSelected: 'Способ получения не выбран.',
  someProductsAreOnlyAvailableInMoscow: 'Некоторые товары доставляются только по Москве. Оформить заказ можно только после изменения города доставки или их удаления из корзины.',

  // Validation.
  minimumOrderAmountForPaymentViaSbpIs10Rubles: 'Минимальная сумма заказа при оплате через СБП — 10 ₽.',
  passwordDoNotMatch: 'Пароли не совпадают.',
  paymentMethodIsNotSelected: 'Способ оплаты не выбран.',
  shippingAddressLine1CannotBeEmpty: 'Поле «Название улицы и номер дома» не может быть пустым.',
  shippingAddressLine1ContainsNonLatinLetters: 'В поле «Название улицы и номер дома» должны быть только латинские буквы.',
  shippingAddressLine2ContainsNonLatinLetters: 'В поле «Номер квартиры/офиса» должны быть только латинские буквы.',
  shippingCityCannotBeEmpty: 'Поле «Город / населённый пункт» не может быть пустым.',
  shippingCityContainsNonLatinLetters: 'В поле «Город / населённый пункт» должны быть только латинские буквы.',
  shippingCountryCannotBeEmpty: 'Поле «Страна» не может быть пустым.',
  shippingEmailFieldCannotBeEmpty: 'Поле «Email» не может быть пустым.',
  shippingFirstNameFieldCannotBeEmpty: 'Поле «Имя» не может быть пустым.',
  shippingFirstNameContainsNonLatinLetters: 'В поле «Имя» должны быть только латинские буквы.',
  shippingLastNameFieldCannotBeEmpty: 'Поле «Фамилия» не может быть пустым.',
  shippingLastNameContainsNonLatinLetters: 'В поле «Фамилия» должны быть только латинские буквы.',
  shippingPhoneNumberFieldCannotBeEmpty: 'Поле «Номер телефона» не может быть пустым.',
  shippingPhoneNumberFieldContainsInvalidCharacters: 'Поле «Номер телефона» содержит недопустимые символы.',
  shippingPostcodeCannotBeEmpty: 'Поле «Почтовый индекс» не может быть пустым.',
  shippingPostcodeContainsNonLatinLetters: 'В поле «Почтовый индекс» должны быть только латинские буквы.',
  shippingStateCannotBeEmpty: 'Поле «Регион/штат/провинция» не может быть пустым.',
  shippingStateContainsNonLatinLetters: 'В поле «Регион/штат/провинция» должны быть только латинские буквы.',
  youNeedToAgreeToPreOrder: 'Необходимо согласиться с предзаказом.',
  youNeedToAgreeToTermsAndConditions: 'Необходимо согласиться с правилами.',

  failedToCreateOrder: 'Не удалось создать заказ. Обновите страницу и повторите попытку.',
  orderIsConfirmed: 'Заказ подтверждён.'
}

export const useCartStore = defineStore('cart', {
  state: () => initialState(),
  getters: {
    areSomeProductsOnlyAvailableInMoscow (state: State): boolean {
      return state.cartProductsData.some((x: CartProductData): boolean => x.attributes.isOnlyAvailableInMoscow)
    },
    backToSchoolTicketsCount (state: State): number | null {
      if (state.backToSchoolStepAmountInKopecks === null || state.backToSchoolStepAmountInKopecks === 0) {
        return null
      }

      return Math.floor(this.totalInKopecks / state.backToSchoolStepAmountInKopecks)
    },
    cartOrderShippingType (state: State): CartOrderShippingType | null {
      return state.cartOrderShippingTypes.find((x: CartOrderShippingType): boolean => x.id === state.orderShippingType)
        ?? null
    },
    finalShippingCostInKopecks (state: State): number | null {
      if (this.isFreeShippingEnabled) {
        return 0
      }

      return this.fixedShippingCostInKopecks ?? state.shippingCostInKopecks
    },
    fixedShippingCostInKopecks (): number | null {
      return this.cartOrderShippingType?.shippingCostInKopecks ?? null
    },
    getCartProductDataById: (state: State) => (cartProductId: string): CartProductData | null => {
      return state.cartProductsData.find(({ id }): boolean => id === cartProductId) || null
    },
    goldenTicketsCount (state: State): number | null {
      if (state.goldenTicketStepAmountInKopecks === null || state.goldenTicketStepAmountInKopecks === 0) {
        return null
      }

      return Math.floor(this.totalInKopecks / state.goldenTicketStepAmountInKopecks)
    },
    preOrderCartProductsData (state: State): CartProductData[] {
      return state.cartProductsData.filter((x: CartProductData): boolean => Boolean(x.meta.isOnPreOrder))
    },
    isFreeShippingEnabled (): boolean {
      return this.isFreeShippingEnabledByPromoCode
        || this.isFreeShippingEnabledBySbpPayment
        || this.isFreeShippingEnabledBySubtotal
    },
    isFreeShippingEnabledByPromoCode (state: State): boolean {
      return state.orderShippingType !== null
        && OrderShippingType.isDeliveryToPickUpPoint(state.orderShippingType)
        && (
          Boolean(state.isFreeShippingOnFirstOrderEnabled)
          || (Boolean(state.isFreeShippingAllowed) && !state.isFreeShippingOnFirstOrderAllowed)
        )
    },
    isFreeShippingEnabledBySbpPayment (state: State): boolean {
      return state.orderShippingType !== null
        && OrderShippingType.isDeliveryToPickUpPoint(state.orderShippingType)
        && state.orderPaymentMethod === OrderPaymentMethod.CloudPaymentsSbp
    },
    isFreeShippingEnabledBySubtotal (): boolean {
      return this.minOrderSubtotalAmountInKopecksForFreeShipping !== null
        && this.subtotalWithDiscountInKopecks >= this.minOrderSubtotalAmountInKopecksForFreeShipping
    },
    isShippingCostKnown (): boolean {
      return this.isFreeShippingEnabled || this.fixedShippingCostInKopecks !== null
    },
    minOrderSubtotalAmountInKopecksForFreeShipping (): number | null {
      return this.cartOrderShippingType?.minOrderSubtotalAmountInKopecksForFreeShipping ?? null
    },
    subtotalInKopecks (state: State): number {
      return state.cartProductsData.reduce(
        (a: number, x: CartProductData): number => a
          + x.meta.quantity
          * (helpers.convertRublesToKopecks(helpers.getProductPrice(x)) ?? 0),
        0
      )
    },
    subtotalWithDiscountInKopecks (state: State): number {
      return this.subtotalInKopecks - (state.discountAmountInKopecks ?? 0)
    },
    totalInKopecks (state: State): number {
      return this.subtotalWithDiscountInKopecks
        + (this.finalShippingCostInKopecks ?? 0)
        + (state.isUrgentOrder && state.urgentOrderPriceInKopecks !== null ? state.urgentOrderPriceInKopecks : 0)
    },
    totalItems (state: State): number {
      return state.cartProductsData.reduce((a: number, x: CartProductData): number => a + x.meta.quantity, 0)
    }
  },
  actions: {
    async fetchCartProducts (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()
      const productsStore = useProductsStore()

      const {
        certificateIsCanceled,
        failedToGetCartProducts,
        promoCodeIsApplied,
        promoCodeIsCanceled,
        redemptionOfPointsHasBeenCanceled,
        redemptionOfSuperminsPointsHasBeenCanceled
      } = messages

      try {
        const response = await $apiHelper.cartProducts.getCartProducts(this.getParams())
        const { data, meta } = JsonApiHelper.denormalizeResponse(response)

        // Products.
        this.cartProductsData = data.map((x: CartProductData): CartProductData => {
          x.meta.isSelected = true

          return x
        })

        productsStore.setCartTotalItems(this.totalItems)
        productsStore.clearCartProducts()

        for (const { id, meta } of data) {
          productsStore.addCartProduct({ id, quantity: meta.quantity })
        }

        // Discounts.
        this.discountAmountInKopecks = meta.discountAmountInKopecks
        this.errorDetail = meta.errorDetail

        if (this.errorDetail) {
          if (this.isPromoCodeApplying) {
            if (this.cartProductsData.length && this.errorDetail) {
              $toast.error(this.errorDetail)
            }
          } else if (this.isPromoCodeApplied) {
            this.isPromoCodeApplied = false

            $toast.warning(promoCodeIsCanceled)
          }

          if (this.arePointsApplied) {
            this.arePointsApplied = false

            $toast.warning(redemptionOfPointsHasBeenCanceled)
          }

          if (this.areSuperminsPointsApplied) {
            this.areSuperminsPointsApplied = false

            $toast.warning(redemptionOfSuperminsPointsHasBeenCanceled)
          }

          if (this.isCertificateApplied) {
            this.isCertificateApplied = false

            $toast.warning(certificateIsCanceled)
          }
        } else if (this.isPromoCodeApplying) {
          this.isPromoCodeApplied = true

          $toast.success(promoCodeIsApplied)
        }

        // Points.
        this.cashbackInPoints = meta.cashbackInPoints ?? null
        this.maxPointsForOrder = meta.maxPointsForOrder ?? null
        this.userTotalPoints = meta.userTotalPoints ?? null

        // Supermins points.
        this.cashbackInSuperminsPoints = meta.cashbackInSuperminsPoints ?? null
        this.maxSuperminsPointsForOrder = meta.maxSuperminsPointsForOrder ?? null
        this.userTotalSuperminsPoints = meta.userTotalSuperminsPoints ?? null

        this.backToSchoolStepAmountInKopecks = meta.backToSchoolStepAmountInKopecks ?? null
        this.gifts = Array.isArray(meta.gifts)
          ? meta.gifts.map((x: Gift): Gift => ({
            id: x.id,
            maxSubtotalInKopecks: x.maxSubtotalInKopecks ?? Infinity,
            minSubtotalInKopecks: x.minSubtotalInKopecks ?? 0,
            name: x.name
          }))
          : []
        this.goldenTicketStepAmountInKopecks = meta.goldenTicketStepAmountInKopecks ?? null
        this.isFreeShippingAllowed = meta.isFreeShippingAllowed
        this.isFreeShippingOnFirstOrderAllowed = meta.isFreeShippingOnFirstOrderAllowed
        this.isFreeShippingOnFirstOrderEnabled = meta.isFreeShippingOnFirstOrderEnabled

        if (meta.notifications && Array.isArray(meta.notifications)) {
          this.notifications = meta.notifications
        }

        if (
          this.maxPointsForOrder !== null
          && (
            this.form.pointsToSpend === 0
            || (this.form.pointsToSpend !== null && this.form.pointsToSpend > this.maxPointsForOrder)
          )
        ) {
          this.form.pointsToSpend = this.maxPointsForOrder
        }

        if (
          this.maxSuperminsPointsForOrder !== null
          && (
            this.form.superminsPointsToSpend === 0
            || (
              this.form.superminsPointsToSpend !== null
              && this.form.superminsPointsToSpend > this.maxSuperminsPointsForOrder
            )
          )
        ) {
          this.form.superminsPointsToSpend = this.maxSuperminsPointsForOrder
        }

        if (meta.orderPaymentMethods && Array.isArray(meta.orderPaymentMethods)) {
          const cartOrderPaymentMethods = meta.orderPaymentMethods
          const cloudPaymentsExists = meta.orderPaymentMethods.some(
            (x: CartOrderPaymentMethod) => x.id === OrderPaymentMethod.CloudPayments
          )
          const cloudPaymentsSbpExists = meta.orderPaymentMethods.some(
            (x: CartOrderPaymentMethod) => x.id === OrderPaymentMethod.CloudPaymentsSbp
          )

          if (cloudPaymentsExists && !cloudPaymentsSbpExists) {
            const cloudPaymentsMethod = meta.orderPaymentMethods.find(
              (x: CartOrderPaymentMethod) => x.id === OrderPaymentMethod.CloudPayments
            )!
            cartOrderPaymentMethods.push({
              id: OrderPaymentMethod.CloudPaymentsSbp,
              maxOrderTotalAmountInKopecks: cloudPaymentsMethod.maxOrderTotalAmountInKopecks ?? null,
              minOrderTotalAmountInKopecks: cloudPaymentsMethod.minOrderTotalAmountInKopecks ?? null
            })
          }

          this.cartOrderPaymentMethods = cartOrderPaymentMethods
        }

        if (meta.orderShippingTypes && Array.isArray(meta.orderShippingTypes)) {
          this.cartOrderShippingTypes = meta.orderShippingTypes
        }

        if (meta.userShippingData) {
          this.userShippingData = meta.userShippingData
        }

        this.urgentOrderPriceInKopecks = meta.urgentOrderPriceInKopecks ?? null

        if (this.urgentOrderPriceInKopecks === null) {
          this.isUrgentOrder = false
        }

        this.weightInGrams = meta.weightInGrams

        this.isDataLoadingError = false
      } catch (error) {
        console.error(error)

        this.isDataLoadingError = true

        $toast.error(failedToGetCartProducts)
      }
    },
    fillForm (): void {
      const {
        addressLine1, addressLine2, city, country, email, firstName, lastName, phoneNumber, postcode, state
      } = this.userShippingData ?? {}

      this.form.shippingAddressLine1 = addressLine1 ?? null
      this.form.shippingAddressLine2 = addressLine2 ?? null
      this.form.shippingCity = city ?? null
      this.form.shippingCountry = country ?? null
      this.form.shippingEmail = email ?? null
      this.form.shippingFirstName = firstName ?? null
      this.form.shippingLastName = lastName ?? null
      this.form.shippingPhoneNumber = phoneNumber ?? null
      this.form.shippingPostcode = postcode ?? null
      this.form.shippingState = state ?? null
    },
    getParams (): GetCartProductsParams {
      const params: GetCartProductsParams = {}

      if (this.form.certificateName && (this.isCertificateApplied || this.isCertificateApplying)) {
        params.certificateName = this.form.certificateName
      }

      if (this.form.pointsToSpend !== null && (this.arePointsApplied || this.isPointsApplying)) {
        params.pointsToSpend = this.form.pointsToSpend
      }

      if (this.form.superminsPointsToSpend !== null && (this.areSuperminsPointsApplied || this.isPointsApplying)) {
        params.superminsPointsToSpend = this.form.superminsPointsToSpend
      }

      if (this.form.promoCodeName && (this.isPromoCodeApplied || this.isPromoCodeApplying)) {
        params.promoCodeName = this.form.promoCodeName
      }

      return params
    },
    async payForOrder (): Promise<void> {
      const { $apiHelper, $toast } = useNuxtApp()

      const { initCloudPaymentsWidget, trackOrder } = usePaymentWidget()

      if (!this.validateShipping()) {
        return
      }

      const {
        password,
        passwordConfirm,
        shippingAddressLine1,
        shippingAddressLine2,
        shippingCity,
        shippingCountry,
        shippingEmail,
        shippingFirstName,
        shippingLastName,
        shippingPhoneNumber,
        shippingPostcode,
        shippingState
      } = this.form
      const {
        minimumOrderAmountForPaymentViaSbpIs10Rubles,
        passwordDoNotMatch,
        paymentMethodIsNotSelected,
        shippingAddressLine1CannotBeEmpty,
        shippingAddressLine1ContainsNonLatinLetters,
        shippingAddressLine2ContainsNonLatinLetters,
        shippingCityCannotBeEmpty,
        shippingCityContainsNonLatinLetters,
        shippingCountryCannotBeEmpty,
        shippingEmailFieldCannotBeEmpty,
        shippingFirstNameFieldCannotBeEmpty,
        shippingFirstNameContainsNonLatinLetters,
        shippingLastNameFieldCannotBeEmpty,
        shippingLastNameContainsNonLatinLetters,
        shippingPhoneNumberFieldCannotBeEmpty,
        shippingPhoneNumberFieldContainsInvalidCharacters,
        shippingPostcodeCannotBeEmpty,
        shippingPostcodeContainsNonLatinLetters,
        shippingStateCannotBeEmpty,
        shippingStateContainsNonLatinLetters,
        youNeedToAgreeToPreOrder,
        youNeedToAgreeToTermsAndConditions,

        failedToCreateOrder,
        orderIsConfirmed
      } = messages

      const errors: { condition: boolean, message: string }[] = [
        { condition: (password ?? '') !== (passwordConfirm ?? ''), message: passwordDoNotMatch },
        { condition: !shippingFirstName, message: shippingFirstNameFieldCannotBeEmpty },
        { condition: !shippingLastName, message: shippingLastNameFieldCannotBeEmpty },
        { condition: !shippingEmail, message: shippingEmailFieldCannotBeEmpty },
        { condition: !shippingPhoneNumber, message: shippingPhoneNumberFieldCannotBeEmpty },
        {
          condition: !/^[\d\s\-+()]*$/.test(String(shippingPhoneNumber)),
          message: shippingPhoneNumberFieldContainsInvalidCharacters
        }
      ]

      const {
        BoxberryCourier,
        BoxberryPickUpPoint,
        CdekPickupPoint,
        DalliCourier,
        DostavistaInsideMkad,
        DostavistaOutsideMkad,
        FivePostPickUpPoint,
        LocalPickUp,
        MagnitPostPickupPoint,
        RussianPostCourier,
        RussianPostInternationalParcel,
        RussianPostPickUpPoint,
        YandexDeliveryPickUpPoint
      } = OrderShippingType

      if (
        this.orderShippingType !== null
        && [BoxberryCourier, DostavistaInsideMkad, DostavistaOutsideMkad].includes(this.orderShippingType)
      ) {
        errors.push(...[
          { condition: !shippingPostcode, message: shippingPostcodeCannotBeEmpty },
          { condition: !shippingCountry, message: shippingCountryCannotBeEmpty },
          { condition: !shippingState, message: shippingStateCannotBeEmpty },
          { condition: !shippingCity, message: shippingCityCannotBeEmpty },
          { condition: !shippingAddressLine1, message: shippingAddressLine1CannotBeEmpty }
        ])
      } else if (this.orderShippingType === RussianPostInternationalParcel) {
        // eslint-disable-next-line no-control-regex
        const regularExpression = /^[\x00-\x7F]*$/
        errors.push(...[
          { condition: !shippingPostcode, message: shippingPostcodeCannotBeEmpty },
          { condition: !shippingCity, message: shippingCityCannotBeEmpty },
          { condition: !shippingAddressLine1, message: shippingAddressLine1CannotBeEmpty },
          { condition: !regularExpression.test(String(shippingFirstName)), message: shippingFirstNameContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingLastName)), message: shippingLastNameContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingPostcode)), message: shippingPostcodeContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingState)), message: shippingStateContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingCity)), message: shippingCityContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingAddressLine1)), message: shippingAddressLine1ContainsNonLatinLetters },
          { condition: !regularExpression.test(String(shippingAddressLine2)), message: shippingAddressLine2ContainsNonLatinLetters }
        ])
      }

      errors.push(...[
        { condition: this.orderPaymentMethod === null, message: paymentMethodIsNotSelected },
        { condition: !this.form.isAgreeToTermsAndConditions, message: youNeedToAgreeToTermsAndConditions },
        {
          condition: this.preOrderCartProductsData.length > 0 && !this.form.isAgreeToPreOrder,
          message: youNeedToAgreeToPreOrder
        },
        {
          condition: this.orderPaymentMethod === OrderPaymentMethod.CloudPaymentsSbp
            && this.totalInKopecks < constants.minOrderTotalAmountForSbpPaymentInKopecks,
          message: minimumOrderAmountForPaymentViaSbpIs10Rubles
        }
      ])

      for (const { condition, message } of errors) {
        if (condition) {
          $toast.error(message)

          return
        }
      }

      const data: CreateOrderRequestData = {
        password: password,
        paymentMethodId: this.orderPaymentMethod === OrderPaymentMethod.CloudPaymentsSbp
          ? OrderPaymentMethod.CloudPayments
          : this.orderPaymentMethod!,
        shippingCostInKopecks: this.finalShippingCostInKopecks ?? 0,
        shippingEmail: shippingEmail!,
        shippingFirstName: shippingFirstName!,
        shippingLastName: shippingLastName!,
        shippingPhoneNumber: shippingPhoneNumber!,
        shippingTypeId: this.orderShippingType!
      }

      if (this.orderPaymentMethod === OrderPaymentMethod.CloudPayments) {
        data.useCloudPaymentsWidgetData = 'yes'
      } else if (this.orderPaymentMethod === OrderPaymentMethod.CloudPaymentsSbp) {
        data.useSbpPayment = 'yes'
      }

      switch (this.orderShippingType) {
        case BoxberryPickUpPoint:
          data.boxberryPickUpPointCode = (this.shippingData as PickupPointShippingData).pickupPointId
          break
        case CdekPickupPoint:
        case MagnitPostPickupPoint: {
          const { pickupPointId } = this.shippingData as PickupPointShippingData
          data.shippingData = { pickupPointId }
          break
        }
        case DalliCourier: {
          const {
            dalliAddress, dalliDate, dalliMinTime, dalliMaxTime, dalliServiceCode, dalliZone
          } = this.shippingData as DalliCourierData

          data.shippingData = {
            dalliAddress,
            dalliDate,
            dalliMinTime,
            dalliMaxTime,
            dalliServiceCode,
            dalliZone
          }
          break
        }
        case DostavistaInsideMkad:
        case DostavistaOutsideMkad:
          data.shippingAddressLine1 = shippingAddressLine1
          data.shippingAddressLine2 = shippingAddressLine2
          data.shippingCity = shippingCity
          data.shippingCountry = shippingCountry
          data.shippingPostcode = shippingPostcode
          data.shippingState = shippingState
          break
        case FivePostPickUpPoint:
        case LocalPickUp:
        case YandexDeliveryPickUpPoint: {
          const { pickupPointId } = this.shippingData as PickupPointShippingData
          data.shippingData = { pickUpPointId: pickupPointId }
          break
        }
        case RussianPostCourier: {
          const {
            address, area, comment, details, mailType, place, postcode, region
          } = this.shippingData as RussianPostCourierShippingData
          data.russianPostAddressAddress = address
          data.russianPostAddressArea = area
          data.russianPostAddressComment = comment
          data.russianPostAddressDetails = details
          data.russianPostAddressMailType = mailType
          data.russianPostAddressPlace = place
          data.russianPostAddressPostcode = postcode
          data.russianPostAddressRegion = region
          break
        }
        case RussianPostInternationalParcel: {
          const { countryId, countryName } = this.shippingData as RussianPostInternationalParcelShippingData
          data.shippingAddressLine1 = shippingAddressLine1
          data.shippingAddressLine2 = shippingAddressLine2
          data.shippingCity = shippingCity
          data.shippingData = { countryId }
          data.shippingCountry = countryName
          data.shippingPostcode = shippingPostcode
          data.shippingState = shippingState
          break
        }
        case RussianPostPickUpPoint: {
          const { fullAddress, pickupPointId } = this.shippingData as PickupPointShippingData
          data.russianPostPickUpPointAddress = fullAddress
          data.russianPostPickUpPointPostcode = pickupPointId
          break
        }
        default:
          break
      }

      if (this.isPromoCodeApplied && this.form.promoCodeName) {
        data.promoCodeName = this.form.promoCodeName
      }

      if (this.arePointsApplied && this.form.pointsToSpend !== null && this.form.pointsToSpend > 0) {
        data.pointsToSpend = this.form.pointsToSpend
      }

      if (
        this.areSuperminsPointsApplied
        && this.form.superminsPointsToSpend !== null
        && this.form.superminsPointsToSpend > 0
      ) {
        data.superminsPointsToSpend = this.form.superminsPointsToSpend
      }

      if (this.isCertificateApplied && this.form.certificateName) {
        data.certificateName = this.form.certificateName
      }

      const utmParameters = helpers.getUtmParameters()

      if (utmParameters) {
        const admitadUid = useCookie('admitad_uid').value

        if (typeof admitadUid === 'string') {
          utmParameters.admitadUid = admitadUid
        }

        const cityAdsClickId = useCookie('cityads_click_id').value

        if (typeof cityAdsClickId === 'string') {
          utmParameters.cityAdsClickId = cityAdsClickId
        }

        data.utmParameters = utmParameters
      }

      if (this.isBloggerOrder) {
        data.isBloggerOrder = 'yes'
      }

      if (this.isRepeatOrder) {
        data.isRepeatOrder = 'yes'
      }

      if (this.isUrgentOrder) {
        data.isUrgentOrder = 'yes'
      }

      let orderData = null

      this.isOrderCreating = true

      try {
        const response = await $apiHelper.orders.createOrder(data)

        orderData = response.data

        $toast.success(orderIsConfirmed)
      } catch (error) {
        ApiErrorHandler.handleFetchError(error as FetchError, failedToCreateOrder)
      }

      if (orderData) {
        const { id: orderId, meta } = orderData
        const { paymentUrl } = meta

        if (
          this.orderPaymentMethod === OrderPaymentMethod.CloudPayments
          && meta.cloudPaymentsWidgetData
        ) {
          initCloudPaymentsWidget(meta.cloudPaymentsWidgetData, orderId)
        } else {
          trackOrder(orderId)

          // Wait 5 seconds before redirecting to have time to track the order.
          setTimeout(
            async () => {
              if (paymentUrl) {
                window.location.replace(paymentUrl)
              } else {
                await navigateTo(Page.AccountOrders)
              }

              this.isOrderCreating = false
            },
            5000
          )
        }
      } else {
        this.isOrderCreating = false
      }
    },
    trackCartByEcommerce (page: Page): void {
      if (!this.cartProductsData.length) {
        return
      }

      const ecommerceCartItems = []

      for (const x of this.cartProductsData) {
        const { attributes, id, meta } = x
        const { name } = attributes
        const { brandName, categoryNames, quantity } = meta
        const discountedPrice = helpers.getProductDiscountedPrice(x) ?? 0

        const ecommerceCartItem: EcommerceCartItem = { item_id: id, item_name: name, price: discountedPrice, quantity }

        if (typeof brandName === 'string') {
          ecommerceCartItem.item_brand = brandName
        }

        if (categoryNames && Array.isArray(categoryNames)) {
          ecommerceCartItem.item_category = categoryNames.sort()
        }

        ecommerceCartItems.push(ecommerceCartItem)
      }

      const total = helpers.convertKopecksToRubles(this.totalInKopecks)!

      switch (page) {
        case Page.Cart:
          EcommerceApiService.viewCart(ecommerceCartItems, total)
          break
        case Page.Checkout:
          EcommerceApiService.beginCheckout(ecommerceCartItems, total, this.form.promoCodeName)
          break
        default:
          break
      }
    },
    updateForm (): void {
      if (this.userShippingData) {
        this.fillForm()
      }

      const { DostavistaInsideMkad, DostavistaOutsideMkad, RussianPostInternationalParcel } = OrderShippingType

      switch (this.orderShippingType) {
        case DostavistaInsideMkad:
          this.form.shippingCity = 'Москва'
          this.form.shippingCountry = 'Россия'
          this.form.shippingState = 'г. Москва'
          break
        case DostavistaOutsideMkad:
          this.form.shippingCountry = 'Россия'
          break
        case RussianPostInternationalParcel:
          if (this.shippingData) {
            this.form.shippingCountry = (this.shippingData as RussianPostInternationalParcelShippingData).countryName
          }

          break
      }
    },
    validateShipping (): boolean {
      const { $toast } = useNuxtApp()

      const { LocalPickUp, RussianPostCourier, RussianPostInternationalParcel } = OrderShippingType
      const {
        addressIsNotFound,
        addressIsNotSelected,
        countryIsNotSelected,
        localPickUpPointIsNotSelected,
        orderSubtotalAmountIsTooSmall,
        pickUpPointIsNotSelected,
        shippingTypeIsNotSelected,
        someProductsAreOnlyAvailableInMoscow
      } = messages

      const showErrorAndExit = (message: string): boolean => {
        $toast.error(message)

        return false
      }

      for (const { isError, text } of this.notifications) {
        if (isError !== undefined && text !== undefined && isError && text) {
          return showErrorAndExit(text)
        }
      }

      if (this.orderShippingType === null) {
        return showErrorAndExit(shippingTypeIsNotSelected)
      }

      if (OrderShippingType.isDeliveryToPickUpPoint(this.orderShippingType) && !this.shippingData) {
        return showErrorAndExit(pickUpPointIsNotSelected)
      }

      if (this.orderShippingType === LocalPickUp && !this.shippingData) {
        return showErrorAndExit(localPickUpPointIsNotSelected)
      }

      if (this.orderShippingType === RussianPostCourier) {
        if (!this.shippingData) {
          return showErrorAndExit(addressIsNotSelected)
        }

        if (this.shippingCostInKopecks === null) {
          return showErrorAndExit(addressIsNotFound)
        }
      }

      if (this.orderShippingType === RussianPostInternationalParcel && !this.shippingData) {
        return showErrorAndExit(countryIsNotSelected)
      }

      if (
        this.areSomeProductsOnlyAvailableInMoscow
        && OrderShippingType.isDeliveryToMoscow(this.orderShippingType, this.shippingData) === false
      ) {
        return showErrorAndExit(someProductsAreOnlyAvailableInMoscow)
      }

      if (this.subtotalWithDiscountInKopecks < constants.minOrderSubtotalAmountInKopecks) {
        return showErrorAndExit(orderSubtotalAmountIsTooSmall)
      }

      return true
    }
  }
})
